import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import dailyNotificationService from '../services/dailyNotificationService';

const DailyNotificationManager = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSocialVisitorNotification, setShowSocialVisitorNotification] = useState(false);

  useEffect(() => {
    // Check for social visitor on first load
    const checkSocialVisitor = () => {
      if (dailyNotificationService.detectSocialVisitor()) {
        // Show social visitor notification after a short delay
        setTimeout(() => {
          setShowSocialVisitorNotification(true);
        }, 3000);
      }
    };

    // Initialize notification service
    const initializeNotifications = async () => {
      try {
        const initialized = await dailyNotificationService.initialize();
        setIsInitialized(initialized);
        
        if (initialized) {
          // Update user status
          dailyNotificationService.updateUserStatus(isAuthenticated, user);
          
          // Get current permission status
          const status = dailyNotificationService.getPermissionStatus();
          setPermissionStatus(status);
          
          // Check for social visitor
          checkSocialVisitor();
        }
      } catch (error) {
        console.error('Failed to initialize daily notifications:', error);
      }
    };

    // Only initialize if not loading
    if (!isLoading) {
      initializeNotifications();
    }
  }, [isLoading]);

  // Update user status when auth state changes
  useEffect(() => {
    if (isInitialized && !isLoading) {
      dailyNotificationService.updateUserStatus(isAuthenticated, user);
    }
  }, [isAuthenticated, user, isInitialized, isLoading]);

  // Handle notification permission request
  const requestNotificationPermission = async () => {
    try {
      const permission = await dailyNotificationService.requestPermission();
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        // Reinitialize with granted permission
        const initialized = await dailyNotificationService.initialize();
        setIsInitialized(initialized);
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  };

  // Handle social visitor notification
  const handleSocialVisitorNotification = async () => {
    const notification = dailyNotificationService.getNextNotification();
    if (notification && notification.userType === 'socialVisitor') {
      try {
        await dailyNotificationService.showImmediateNotification();
        dailyNotificationService.markSocialVisitorHandled();
        setShowSocialVisitorNotification(false);
      } catch (error) {
        console.error('Failed to show social visitor notification:', error);
      }
    }
  };

  // Show social visitor banner if detected
  if (showSocialVisitorNotification) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7m0 0v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium">
                Welcome! Your friend shared something special with you! ðŸŽ
              </p>
              <p className="text-xs opacity-90">
                See why they wanted you to discover Tinat Academy
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSocialVisitorNotification}
              className="bg-white text-purple-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-50 transition-colors duration-200"
            >
              Discover Now
            </button>
            <button
              onClick={() => {
                dailyNotificationService.markSocialVisitorHandled();
                setShowSocialVisitorNotification(false);
              }}
              className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show permission banner if needed
  const shouldShowPermissionBanner = permissionStatus === 'default' && isInitialized && !isLoading && !showSocialVisitorNotification;

  if (shouldShowPermissionBanner) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-emerald-600 text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium">
                Stay on track with daily learning reminders!
              </p>
              <p className="text-xs opacity-90">
                Get notified about your studies and new content every day
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={requestNotificationPermission}
              className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors duration-200"
            >
              Enable Notifications
            </button>
            <button
              onClick={() => setPermissionStatus('denied')}
              className="text-white/80 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DailyNotificationManager;
